import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRequest, ResponseDataType } from 'api';
import {
    Card,
    Row,
    Col,
    Table,
    Modal,
    Button,
    List,
    Space,
    Pagination,
} from 'antd';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/es/table';

interface ClassYearData {
    classYearId: number;
    className: string;
    classLevel: string;
    schoolName: string;
    schoolYear: string;
}

interface StudentData {
    studentId: number;
    name: string;
    dob: string;
    gender: number;
    studentCode: string;
    cmnd: string;
}

interface ParentData {
    name: string;
    phoneNumber: string;
    email: string;
    cmnd: string;
}

export const SubjectClassDetail = () => {
    const { classYearId } = useParams<{ classYearId: string }>();

    const [classInfo, setClassInfo] = useState<ClassYearData | null>(null);
    const [students, setStudents] = useState<StudentData[]>([]);
    const [parents, setParents] = useState<ParentData[]>([]);
    const [parentModalVisible, setParentModalVisible] = useState(false);
    const [loadingParents, setLoadingParents] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    useEffect(() => {
        if (classYearId) {
            fetchClassInfo(classYearId);
            fetchStudents(classYearId);
        }
    }, [classYearId]);

    const fetchClassInfo = async (id: string) => {
        const response: ResponseDataType<ClassYearData> = await getRequest(`/cms/class/get-class-year-info?classYearId=${id}`);
        if (response?.code === 200 && response.info) {
            setClassInfo(response.info);
        } else {
            toast.error('Không thể tải thông tin lớp học');
        }
    };

    const fetchStudents = async (id: string) => {
        const response: ResponseDataType<{ list: StudentData[] }> = await getRequest(`/cms/student/get-students-by-class-year?classYearId=${id}`);
        if (
            response?.code === 200 &&
            response.info &&
            typeof response.info === 'object' &&
            Array.isArray((response.info as any).list)
        ) {
            setStudents((response.info as any).list);
        } else {
            toast.error('Không thể tải danh sách học sinh');
            setStudents([]);
        }

    };

    const fetchParentsByStudent = async (studentId: number) => {
        setLoadingParents(true);
        const response: ResponseDataType<ParentData[]> = await getRequest(`/cms/student/get-parent-by-student?studentId=${studentId}`);
        if (response?.code === 200 && Array.isArray(response.info)) {
            setParents(response.info);
            setParentModalVisible(true);
        } else {
            setParents([]);
            toast.error('Không thể tải danh sách phụ huynh');
        }
        setLoadingParents(false);
    };

    const columns: ColumnsType<StudentData> = [
        {
            title: 'Tên học sinh',
            dataIndex: 'name',
            key: 'name',
            align: 'center' as const,
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'dob',
            key: 'dob',
            align: 'center' as const,
        },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            align: 'center' as const,
            render: (gender: number) => (gender === 1 ? 'Nam' : 'Nữ'),
        },
        {
            title: 'Mã học sinh',
            dataIndex: 'studentCode',
            key: 'studentCode',
            align: 'center' as const,
        },
        {
            title: 'CCCD',
            dataIndex: 'cmnd',
            key: 'cmnd',
            align: 'center' as const,
        },
        {
            title: 'Phụ huynh',
            key: 'action',
            align: 'center' as const,
            render: (_: any, record: StudentData) => (
                <Button type="link" onClick={() => fetchParentsByStudent(record.studentId)}
                    style={{
                        border: '1px solid #1890ff',
                        color: '#1890ff',
                        borderRadius: 4,
                        background: 'white',
                    }}
                >
                    Thông tin phụ huynh
                </Button>
            ),
        },
    ];

    return (
        <div>
            <Card style={{ marginBottom: 24 }}>
                {classInfo ? (
                    <Row gutter={[16, 16]}>
                        <Col span={12}><strong>Lớp:</strong> {classInfo.className}</Col>
                        <Col span={12}><strong>Khối:</strong> {classInfo.classLevel}</Col>
                        <Col span={12}><strong>Năm học:</strong> {classInfo.schoolYear}</Col>
                        <Col span={12}><strong>Trường:</strong> {classInfo.schoolName}</Col>
                    </Row>
                ) : (
                    <div>Đang tải thông tin lớp...</div>
                )}
            </Card>

            <h3>Danh sách học sinh</h3>

            <Table
                columns={columns}
                dataSource={students.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                rowKey="studentId"
                pagination={false}
                locale={{ emptyText: 'Không có học sinh' }}
            />

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={students.length}
                    showSizeChanger
                    pageSizeOptions={['5', '10', '20']}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                />
            </div>

            <Modal
                title="Thông tin phụ huynh"
                open={parentModalVisible}
                onCancel={() => setParentModalVisible(false)}
                footer={null}
            >
                {loadingParents ? (
                    <p>Đang tải thông tin...</p>
                ) : parents.length > 0 ? (
                    <List
                        style={{ marginTop: 16 }}
                        dataSource={parents}
                        bordered
                        renderItem={(item) => (
                            <List.Item style={{ border: '1px solid #1890ff', borderRadius: 8, padding: 16 }}>
                                <div>
                                    <p><strong>Họ tên:</strong> {item.name}</p>
                                    <p><strong>SĐT:</strong> {item.phoneNumber}</p>
                                    <p><strong>Email:</strong> {item.email}</p>

                                </div>
                            </List.Item>
                        )}
                    />
                ) : (
                    <p>Không có phụ huynh nào.</p>
                )}
            </Modal>
        </div>
    );
};
